// react
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES, DYNAMIC_ROUTES } from 'router';

// store
import {
  useBookmarkArticleMutation,
  useGetAllArticleBookmarkQuery,
  useLazyGetArticleByIdQuery,
  useLazyGetArticleQuery,
} from 'store/api/cms';
import { Article as ArticleType } from '@karehero/models';

// components
import ArticleContentPage from '@karehero/llama/components/pages/ArticleContent/ArticleContent';

// tracking
import { TrackingEvent, useTracking } from 'hooks';

/**
 * ArticleContent shows the content of an Article from Sanity.
 */
export const ArticleContent = () => {
  // state
  const [article, setArticle] = useState<ArticleType>();

  // hooks
  const { track } = useTracking();
  const { articleId } = useParams();
  const [getArticle, { isLoading: isArticleLoading }] =
    useLazyGetArticleQuery();
  const [getArticleById] = useLazyGetArticleByIdQuery();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [bookmarkArticle] = useBookmarkArticleMutation();
  const { data: articleBookmarks } = useGetAllArticleBookmarkQuery();

  // effects
  useEffect(() => {
    if (!articleId) {
      navigate(ROUTES.LIBRARY);
      return;
    }

    (async () => {
      // check if uuid
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(articleId)) {
        const { data, error } = await getArticleById(articleId);
        if (error) {
          console.error(error);
          navigate(ROUTES.LIBRARY);
          return;
        }
        if (!data) return;
        const articleSlug = data.slug?.current || '';
        navigate(DYNAMIC_ROUTES.LIBRARY_ARTICLE(articleSlug));
      }

      const { data, error } = await getArticle(articleId);
      if (error) {
        navigate(ROUTES.LIBRARY);
        return;
      }
      if (!data) return;
      setArticle(data);

      track(TrackingEvent.ArticleViewed, {
        id: data._id,
        title: data.title,
        tags: data.tags,
      });
    })();
  }, [articleId, getArticle, getArticleById, navigate, track]);

  return (
    <ArticleContentPage
      content={article}
      isLoading={!article ? true : isArticleLoading}
      isBookmarked={
        !articleBookmarks?.find((a) => article?._id && a._id === article?._id)
      }
      onBookmark={() => article?._id && bookmarkArticle(article._id)}
      onBack={goBack}
    />
  );
};

export default ArticleContent;
